'use client';

import * as React from "react";
import { Bell, CheckCheck } from "lucide-react";
import { cn } from "../../utils/cn";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../Drawer/Drawer";

/**
 * NotificationBell — top-nav bell with unread badge + drawer.
 *
 * MV5 Sprint 2.C — one primitive we drop into web-ide + marketing chrome.
 *
 * Data flow: the parent owns fetching + WS subscription and passes the
 * current notifications + unread count. That keeps the primitive decoupled
 * from the transport (marketing uses REST-only; web-ide uses REST + WS).
 *
 * Selected props deliberately:
 *   · `notifications` — the current page (chronological desc)
 *   · `unreadCount`   — badge value; hidden if 0
 *   · `onMarkAllRead` — bulk action
 *   · `onMarkRead(id)` — per-row action
 *   · `renderCard`    — parent can customise the row render (portal vs
 *                       marketing surfaces differ in navigation targets)
 */

export interface NotificationCardModel {
  id: string;
  kind: string;
  createdAt: string;
  readAt: string | null;
  payload: Record<string, unknown>;
}

export interface NotificationBellProps {
  notifications: NotificationCardModel[];
  unreadCount: number;
  onMarkAllRead?: () => void | Promise<void>;
  onMarkRead?: (id: string) => void | Promise<void>;
  onOpenChange?: (open: boolean) => void;
  renderCard?: (n: NotificationCardModel) => React.ReactNode;
  emptyState?: React.ReactNode;
  className?: string;
  buttonClassName?: string;
  /**
   * Show the "Notification preferences" link at the drawer footer.
   * Parent app owns navigation.
   */
  preferencesHref?: string;
}

export const NotificationBell = React.forwardRef<
  HTMLButtonElement,
  NotificationBellProps
>(function NotificationBell(
  {
    notifications,
    unreadCount,
    onMarkAllRead,
    onMarkRead,
    onOpenChange,
    renderCard,
    emptyState,
    className,
    buttonClassName,
    preferencesHref,
  },
  ref,
) {
  const badgeLabel =
    unreadCount > 99 ? "99+" : unreadCount > 0 ? String(unreadCount) : null;

  return (
    <Drawer onOpenChange={onOpenChange}>
      <DrawerTrigger asChild>
        <button
          ref={ref}
          type="button"
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount} unread`
              : "Notifications"
          }
          className={cn(
            "relative inline-flex items-center justify-center rounded-full",
            "size-9 text-ink-primary hover:bg-chrome-hover transition-colors",
            "outline-none focus-visible:ring-2 focus-visible:ring-accent-violet",
            buttonClassName,
          )}
        >
          <Bell className="size-5" aria-hidden />
          {badgeLabel !== null && (
            <span
              data-slot="notification-badge"
              className={cn(
                "absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1",
                "flex items-center justify-center rounded-full",
                "bg-accent-violet text-white text-[10px] font-semibold",
                "ring-2 ring-chrome-surface",
              )}
              aria-hidden
            >
              {badgeLabel}
            </span>
          )}
        </button>
      </DrawerTrigger>
      <DrawerContent side="end" size="wide" className={className}>
        <DrawerHeader className="flex flex-row items-center justify-between">
          <DrawerTitle>Notifications</DrawerTitle>
          {unreadCount > 0 && onMarkAllRead && (
            <button
              type="button"
              onClick={() => onMarkAllRead?.()}
              className="inline-flex items-center gap-1 text-body-14 text-ink-muted hover:text-ink-primary transition-colors"
            >
              <CheckCheck className="size-4" aria-hidden />
              Mark all read
            </button>
          )}
        </DrawerHeader>
        <DrawerBody className="p-0">
          {notifications.length === 0 ? (
            <div className="p-space-24 text-center text-body-14 text-ink-muted">
              {emptyState ?? "You're all caught up."}
            </div>
          ) : (
            <ul role="list" className="divide-y divide-chrome-border">
              {notifications.map((n) => (
                <li key={n.id}>
                  {renderCard ? (
                    renderCard(n)
                  ) : (
                    <DefaultNotificationRow
                      notification={n}
                      onMarkRead={onMarkRead}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}
        </DrawerBody>
        {preferencesHref && (
          <DrawerFooter>
            <a
              href={preferencesHref}
              className="text-body-14 text-accent-violet hover:underline"
            >
              Notification preferences
            </a>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
});
NotificationBell.displayName = "NotificationBell";

// Default per-row render — parent can override via `renderCard`.
function DefaultNotificationRow({
  notification,
  onMarkRead,
}: {
  notification: NotificationCardModel;
  onMarkRead?: (id: string) => void | Promise<void>;
}) {
  const isUnread = !notification.readAt;
  const title = describeKind(notification.kind);
  const preview = extractPreview(notification.payload);
  const when = new Date(notification.createdAt);
  return (
    <div
      onClick={() => {
        if (isUnread && onMarkRead) void onMarkRead(notification.id);
      }}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && isUnread && onMarkRead) {
          e.preventDefault();
          void onMarkRead(notification.id);
        }
      }}
      role="button"
      tabIndex={0}
      className={cn(
        "flex items-start gap-space-12 p-space-16 cursor-pointer",
        "outline-none focus-visible:bg-chrome-hover hover:bg-chrome-hover transition-colors",
        isUnread && "bg-accent-violet/5",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mt-space-4 size-2 rounded-full flex-shrink-0",
          isUnread ? "bg-accent-violet" : "bg-transparent",
        )}
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-space-8">
          <span className="text-body-14 font-medium text-ink-primary truncate">
            {title}
          </span>
          <time
            className="text-body-12 text-ink-muted flex-shrink-0"
            dateTime={notification.createdAt}
          >
            {formatRelative(when)}
          </time>
        </div>
        {preview && (
          <p className="text-body-14 text-ink-muted mt-space-4 line-clamp-2">
            {preview}
          </p>
        )}
      </div>
    </div>
  );
}

function describeKind(kind: string): string {
  switch (kind) {
    case "instructor_message":
      return "New message";
    case "grade_posted":
      return "Grade posted";
    case "escalation_resolved":
      return "Escalation resolved";
    case "cohort_announcement":
      return "Cohort announcement";
    case "billing_reminder":
      return "Billing reminder";
    case "email_verification_pending":
      return "Verify your email";
    case "payment_processing":
      return "Payment processing";
    case "payment_failed":
      return "Payment failed";
    case "refund_processed":
      return "Refund processed";
    case "grade_appeal_submitted":
      return "New appeal to review";
    case "grade_appeal_decided":
      return "Appeal decision";
    case "autosave":
      return "Autosaved";
    default:
      return kind.replace(/_/g, " ");
  }
}

function extractPreview(payload: Record<string, unknown>): string | null {
  if (typeof payload.preview === "string") return payload.preview;
  if (typeof payload.subject === "string") return payload.subject;
  if (typeof payload.message === "string") return payload.message;
  return null;
}

function formatRelative(when: Date): string {
  const now = Date.now();
  const diffMs = now - when.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d`;
  return when.toLocaleDateString();
}
